import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { Download, Send } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { download } from "@/lib/api";
import { fmtDate, useBriefActions, type BriefDetail } from "@/lib/briefs";

/**
 * The strip above the workspace: the brief's name and one-line summary (both
 * autosave on blur), how far along it is, the PDF, and Submit. The reviewer's
 * label and remark show here too, since they are about the brief as a whole.
 */
export function BriefHeader({ detail, onSubmit }: { detail: BriefDetail; onSubmit: () => void }) {
  const { brief, sections, access, viewing } = detail;
  const actions = useBriefActions(brief.id);
  const canEdit = access.canEdit;

  const [title, setTitle] = useState(brief.title);
  const [summary, setSummary] = useState(brief.summary ?? "");
  const editing = useRef(false);
  useEffect(() => {
    if (!editing.current) {
      setTitle(brief.title);
      setSummary(brief.summary ?? "");
    }
  }, [brief.title, brief.summary]);

  const saveTitle = () => {
    editing.current = false;
    const t = title.trim();
    if (!t) {
      setTitle(brief.title);
      return;
    }
    if (t !== brief.title) actions.patchBrief.mutate({ title: t });
  };
  const saveSummary = () => {
    editing.current = false;
    const s = summary.trim();
    if (s !== (brief.summary ?? "")) actions.patchBrief.mutate({ summary: s || null });
  };

  const notSure = sections.filter((s) => s.coreKey && s.state === "not_sure").length;
  const blurOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => e.key === "Enter" && (e.target as HTMLInputElement).blur();

  return (
    <div className="border-border bg-card/80 border-b px-4 py-3 backdrop-blur lg:px-6">
      <Link to="/" className="text-muted-foreground mb-1 inline-block text-xs hover:underline lg:hidden">
        ← My briefs
      </Link>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          {canEdit ? (
            <Input
              value={title}
              onFocus={() => (editing.current = true)}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={blurOnEnter}
              maxLength={120}
              aria-label="Title"
              className="hover:border-input focus:border-input -ml-1 h-8 border-transparent bg-transparent px-1 text-lg font-semibold"
            />
          ) : (
            <h1 className="text-lg font-semibold">{brief.title}</h1>
          )}
          {canEdit ? (
            <Input
              value={summary}
              onFocus={() => (editing.current = true)}
              onChange={(e) => setSummary(e.target.value)}
              onBlur={saveSummary}
              onKeyDown={blurOnEnter}
              maxLength={160}
              placeholder="One sentence: what this brief is about."
              aria-label="Summary"
              className="text-muted-foreground hover:border-input focus:border-input -ml-1 h-7 border-transparent bg-transparent px-1 text-sm"
            />
          ) : brief.summary ? (
            <p className="text-muted-foreground mt-0.5 text-sm">{brief.summary}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center justify-between gap-3 sm:flex-col sm:items-end sm:gap-1.5">
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                void download(
                  viewing === "version" && detail.version ? `/briefs/${brief.id}/versions/${detail.version}/pdf` : `/briefs/${brief.id}/pdf`,
                  "Solution-Brief.pdf",
                )
              }
            >
              <Download size={14} /> PDF
            </Button>
            {canEdit ? (
              <Button size="sm" onClick={onSubmit}>
                <Send size={14} /> {brief.submissionCount > 0 ? "Resubmit" : "Submit"}
              </Button>
            ) : null}
          </div>
          <div className="text-muted-foreground text-[11px]">
            <span className="text-foreground font-medium">
              {brief.clear} of {brief.core} clear
            </span>
            {notSure > 0 ? ` · ${notSure} not sure` : ""}
            {brief.submissionCount > 0 ? ` · v${brief.submissionCount}${brief.editedSinceSubmit ? ", edited since" : ""}` : " · draft"}
            {viewing === "version" ? ` · viewing v${detail.version} as submitted` : ""}
          </div>
        </div>
      </div>
      {brief.adminLabel || brief.adminRemark ? (
        <div className="bg-accent text-accent-foreground mt-2 rounded-md px-3 py-2 text-xs">
          <span className="font-medium">{brief.adminLabel ?? "A note on your brief"}</span>
          {brief.adminRemark ? <span> — {brief.adminRemark}</span> : null}
          {brief.adminAt ? <span className="opacity-70"> · {fmtDate(brief.adminAt)}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
